import { useEffect, useRef, useState, type ReactNode, type UIEvent } from "react";
import { Editor, type EditorBinding } from "@/components/editor";
import type { CodeDiffViewerProps, CodeEditorProps } from "@/shared/ui/codeEditor/public";
import styles from "./CodeEditor.module.css";

type MonacoEditorModule = typeof import("./MonacoCodeEditor");

let loadedMonacoModule: MonacoEditorModule | null = null;
let monacoModulePromise: Promise<MonacoEditorModule | null> | null = null;

// 편집기와 diff 가 Monaco 청크를 함께 쓰고, 한 번만 받는다. 실패하면 null 로 고정해 textarea
// 폴백을 유지한다. 사내망/CSP 문제가 있어도 본문을 읽고 고칠 방법은 있어야 한다.
function loadMonacoModule(): Promise<MonacoEditorModule | null> {
  if (loadedMonacoModule !== null) {
    return Promise.resolve(loadedMonacoModule);
  }
  monacoModulePromise ??= import("./MonacoCodeEditor")
    .then((module) => {
      loadedMonacoModule = module;
      return module;
    })
    .catch(() => null);
  return monacoModulePromise;
}

function useMonacoModule(): MonacoEditorModule | null {
  const [module, setModule] = useState<MonacoEditorModule | null>(loadedMonacoModule);
  useEffect(() => {
    if (module !== null) {
      return;
    }
    let active = true;
    void loadMonacoModule().then((loaded) => {
      if (active && loaded !== null) {
        setModule(loaded);
      }
    });
    return () => {
      active = false;
    };
  }, [module]);
  return module;
}

// config(YAML) 편집기다. Monaco 를 npm 번들 lazy 청크로 로드한다(MonacoCodeEditor 참조, CDN 미사용).
// 로딩 중이거나 청크 로드가 실패하면(예: 오프라인) textarea 로 폴백한다. Props 는 training-infra 의
// CodeEditor 와 호환을 유지한다.
export function CodeEditor(props: CodeEditorProps): ReactNode {
  const monacoModule = useMonacoModule();
  // 사용자가 로딩 폴백에 바로 입력했으면, blur 전에는 Monaco 로 교체하지 않는다. 포커스 중에
  // DOM 을 교체하면 새 Monaco 가 커서를 본문 끝에 만든다. 그러면 입력 위치가 맨 아래로 이동한다.
  const [fallbackFocused, setFallbackFocused] = useState(false);
  if (monacoModule === null || fallbackFocused) {
    return (
      <TextareaCodeEditor
        {...props}
        onFocus={() => setFallbackFocused(true)}
        onBlur={() => {
          setFallbackFocused(false);
          props.onBlur?.();
        }}
      />
    );
  }
  const MonacoCodeEditor = monacoModule.default;
  return <MonacoCodeEditor {...props} />;
}

// 원본 대비 변경 diff 뷰어(read-only)다. Monaco DiffEditor 를 lazy 로드한다. 실패하면 텍스트로 폴백한다.
export function CodeDiffViewer(props: CodeDiffViewerProps): ReactNode {
  const monacoModule = useMonacoModule();
  if (monacoModule === null) {
    return <TextDiffFallback {...props} />;
  }
  const MonacoDiffViewer = monacoModule.MonacoDiffViewer;
  return <MonacoDiffViewer {...props} />;
}

function TextDiffFallback({ modified }: CodeDiffViewerProps): ReactNode {
  return (
    <TextareaCodeEditor
      value={modified}
      onChange={() => undefined}
      readOnly
      rows={18}
      ariaLabel="제출될 config 본문"
    />
  );
}

function TextareaCodeEditor({
  value,
  onChange,
  rows = 18,
  readOnly = false,
  ariaLabel,
  onFocus,
  onBlur,
}: CodeEditorProps & { onFocus?: () => void }): ReactNode {
  const gutterRef = useRef<HTMLPreElement>(null);
  const lineNumbers = buildLineNumbers(value);
  const controller: EditorBinding = { value, inputDisabled: false, setValue: onChange };
  const syncGutterScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    if (gutterRef.current) {
      gutterRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  };
  return (
    <div className={styles.fallbackShell}>
      <pre ref={gutterRef} className={styles.fallbackLineNumbers} aria-hidden="true">
        {lineNumbers}
      </pre>
      <Editor
        controller={controller}
        className={styles.editor}
        readOnly={readOnly}
        onFocus={onFocus}
        onBlur={onBlur}
        onScroll={syncGutterScroll}
        rows={rows}
        aria-label={ariaLabel}
        spellCheck={false}
      />
    </div>
  );
}

function buildLineNumbers(value: string): string {
  const lineCount = Math.max(1, value.split("\n").length);
  return Array.from({ length: lineCount }, (_, index) => String(index + 1)).join("\n");
}
